import { type FormEvent, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { isProblemError } from '../../api/client';
import { getGetMeQueryKey, getMe, useLogin } from '../../api/generated/identity/identity';
import { LanguageSelector } from '../../app/LanguageSelector';
import { landingPathAfterLogin } from '../../app/routes';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import styles from './auth.module.css';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useLogin();

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    login.mutate(
      { data: { email, password } },
      {
        onSuccess: async (result) => {
          // O cache da pessoa ANTERIOR morre aqui, antes de qualquer outra
          // coisa.
          //
          // Sessao que acaba por expiracao nao passa por "Sair": o 401 leva ao
          // login e deixa todo o cache de quem estava antes vivo por ate
          // `gcTime`. Se outra pessoa entra nessa janela — computador de
          // familia, laboratorio, recepcao —, a primeira renderizacao de
          // /messages, /students ou /billing serve o dado da anterior, e com
          // `staleTime: 30_000` nem refetch em segundo plano acontece: o dado
          // errado fica na tela.
          //
          // Invalidar so `getMe` nao resolve, porque o vazamento nao esta em
          // `getMe` — esta em todas as outras chaves. `clear()` e sincrono e
          // descarta sem refazer requisicao; o que esta tela precisa em seguida
          // ela mesma repoe, e nada do que ficou pertence a quem esta entrando.
          queryClient.clear();

          if (result.status === 'mfa_required') {
            void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
            void navigate('/mfa', { replace: true });
            return;
          }
          if (result.status === 'mfa_enrollment_required') {
            void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
            void navigate('/', { replace: true });
            return;
          }
          // O papel de quem ACABOU de entrar, lido antes de escolher o destino.
          //
          // `getMe` direto, e nao a consulta em cache: o cache ainda guarda o
          // 401 da sessao anterior — e e ela que grava `from` ao cair —, e o
          // destino depende de quem entrou agora. Leitura que falha vira
          // `undefined`, e o destino cai na home, que e o unico que nao depende
          // de papel nenhum.
          const me = await getMe().catch(() => undefined);

          // A sessao recem-lida entra no cache, em vez de so invalidar o que
          // esta la: navegar com o 401 da sessao anterior ainda no cache faz
          // `Authenticated` mandar de volta ao login enquanto o refetch corre, e
          // o caminho retomado se perde nesse desvio.
          if (me === undefined) void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          else queryClient.setQueryData(getGetMeQueryKey(), me);

          const from = (location.state as { from?: string } | null)?.from;
          void navigate(landingPathAfterLogin(from, me?.role), { replace: true });
        },
      },
    );
  };

  const errorMessage = ((): string | undefined => {
    if (!login.isError) return undefined;
    if (!isProblemError(login.error)) return t('error.unexpected');
    if (login.error.status === 429) return t('login.tooManyAttempts');
    if (login.error.status === 401) return t('login.invalidCredentials');
    return t('error.unexpected');
  })();

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('login.title')}</h1>
        <p className={styles.subtitle}>{t('login.subtitle')}</p>

        {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

        <form onSubmit={handleSubmit} noValidate>
          <Field
            label={t('login.email')}
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label={t('login.password')}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button
            className={styles.submit}
            type="submit"
            variant="primary"
            loading={login.isPending}
          >
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        <div className={styles.footer}>
          <LanguageSelector />
        </div>
      </div>
    </main>
  );
}
